// Client-side logger utility
// No longer importing from api.js

const LOG_LEVELS = {
  ERROR: 'error',
  WARN: 'warn',
  INFO: 'info',
  DEBUG: 'debug'
};

const sanitizeUserData = (userData) => {
  if (!userData) return null;
  
  // Only include safe user data
  return {
    id: userData.id || userData.sub,
    email: userData.email ? `***@${userData.email.split('@')[1]}` : undefined,
    role: userData.role,
    preferredCurriculum: userData.preferredCurriculum,
    isActive: userData.isActive,
  };
};

const sanitizeError = (error) => {
  if (!error) return null;
  return {
    message: error.message,
    code: error.code,
    name: error.name,
    stack: import.meta.env.DEV ? error.stack : undefined
  };
};

class Logger {
  constructor() {
    this.isProduction = import.meta.env.PROD;
    this.debugEnabled = import.meta.env.VITE_DEBUG === 'true';
    
    // Construct endpoint directly using build-time env vars
    // Ensure VITE_API_URL is correctly set in your .env / build process
    const apiBaseUrl = import.meta.env.VITE_API_URL || (this.isProduction ? 'https://api.eduvibz.com' : 'http://localhost:5000');
    // Ensure the base URL includes /api if needed (matching api.js logic)
    const baseForLogs = this.isProduction && !apiBaseUrl.endsWith('/api') 
      ? `${apiBaseUrl}/api` 
      : apiBaseUrl;
      
    // Use the derived base URL to construct the log endpoint
    this.logEndpoint = `${baseForLogs.replace(/\/$/, '')}/logs`; // Ensure no double slash
    
    console.log(`[Logger Init] Endpoint configured to: ${this.logEndpoint}`);
    
    this.batchSize = 10;
    this.logQueue = [];
    this.flushInterval = 5000; // 5 seconds
    this.flushFailureCount = 0;
    this.suspendUntil = 0;

    // Setup periodic log flushing
    if (this.isProduction) {
      setInterval(() => this.flushLogs(), this.flushInterval);
    }
  }

  error(message, meta = {}) {
    this._log(LOG_LEVELS.ERROR, message, meta);
  }

  warn(message, meta = {}) {
    this._log(LOG_LEVELS.WARN, message, meta);
  }

  info(message, meta = {}) {
    if (this.isProduction) return;
    this._log(LOG_LEVELS.INFO, message, meta);
  }

  debug(message, meta = {}) {
    // Always log in development, or if debug is explicitly enabled
    if (!this.isProduction || this.debugEnabled) {
      this._log(LOG_LEVELS.DEBUG, message, meta);
      // Force console output even in production for debugging
      this._consoleLog(LOG_LEVELS.DEBUG, {
        timestamp: new Date().toISOString(),
        level: LOG_LEVELS.DEBUG,
        message,
        ...this._processMetadata(meta)
      });
    }
  }

  _log(level, message, meta = {}) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      ...this._processMetadata(meta),
      environment: import.meta.env.MODE || 'development',
      debugEnabled: this.debugEnabled,
      isProduction: this.isProduction,
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    // Always console log in development
    if (!this.isProduction) {
      this._consoleLog(level, logEntry);
    }
    
    // Queue for server in production
    if (this.isProduction) {
      this.queueLog(logEntry);
      // Also console log errors and warnings in production
      if (level === LOG_LEVELS.ERROR || level === LOG_LEVELS.WARN) {
        this._consoleLog(level, logEntry);
      }
    }
  }

  _processMetadata(meta) {
    return {
      ...meta,
      user: meta.user ? sanitizeUserData(meta.user) : undefined,
      error: meta.error ? sanitizeError(meta.error) : undefined
    };
  }

  _consoleLog(level, logEntry) {
    const { timestamp, message, ...rest } = logEntry;
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`;
    
    switch (level) {
      case LOG_LEVELS.ERROR:
        console.error(prefix, message, rest);
        break;
      case LOG_LEVELS.WARN:
        console.warn(prefix, message, rest);
        break;
      case LOG_LEVELS.INFO:
        console.info(prefix, message, rest);
        break;
      case LOG_LEVELS.DEBUG:
        console.debug(prefix, message, rest);
        break;
      default:
        console.log(prefix, message, rest);
    }
  }

  queueLog(logEntry) {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return;
    }

    if (this.suspendUntil > Date.now()) {
      return;
    }

    this.logQueue.push(logEntry);
    
    if (this.logQueue.length >= this.batchSize) {
      this.flushLogs();
    }
  }

  async flushLogs() {
    if (this.logQueue.length === 0) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    if (this.suspendUntil > Date.now()) return;

    const logs = [...this.logQueue];
    this.logQueue = [];

    try {
      const response = await fetch(this.logEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ logs }),
        credentials: 'omit'
      });
      if (!response.ok) {
        throw new Error(`Log endpoint returned ${response.status}`);
      }
      this.flushFailureCount = 0;
    } catch (error) {
      this.flushFailureCount += 1;
      const backoffMs = Math.min(60000, 5000 * this.flushFailureCount);
      this.suspendUntil = Date.now() + backoffMs;
      if (!this.isProduction || this.flushFailureCount <= 2) {
        console.error('Failed to send logs to server:', error);
      }
    }
  }
}

// Export the logger instance directly
export const logger = new Logger();
// Removed configureLoggerEndpoint function
